import React, { useEffect, useMemo, useState } from "react";

import { Camera, Check, Key, PanelLeft } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { changePassword, fetchPublicKey } from "../../Store/Reducers/AuthSlice";
import {
  clearProfilePictureSession,
  updateSidebarPreferences,
  updateUserProfileImageUrl,
  uploadProfilePicture,
} from "../../Store/Reducers/MasterSlice";
import { encryptWithPublicKey } from "../../Utils/Helper/clientEncryption";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
  saveToLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import Button from "../Button";
import Input from "../Input";
import CommonModal from "../Modal";
import {
  SIDEBAR_HOVER_ENABLED_DEFAULT,
  SIDEBAR_NAV_MODE_DEFAULT,
  SIDEBAR_NAV_MODE_OPTIONS,
  SidebarNavMode,
} from "../Sidebar/types";
import TabsComponent from "../Tab";
import { toast } from "../Toast";
import ToggleSwitch from "../ToggleSwitch";

type ProfileSettingsTab = "profile" | "password" | "preferences";

const ALLOWED_PROFILE_MIME_TYPES = ["image/jpeg", "image/png"] as const;
const ALLOWED_PROFILE_EXTENSIONS = [".jpg", ".jpeg", ".png"] as const;

const isAllowedProfilePicture = (file: File): boolean => {
  const mimeOk = (ALLOWED_PROFILE_MIME_TYPES as readonly string[]).includes(
    file.type
  );
  const name = file.name.toLowerCase();
  const extension = name.includes(".") ? `.${name.split(".").pop()}` : "";
  const extensionOk = (
    ALLOWED_PROFILE_EXTENSIONS as readonly string[]
  ).includes(extension);
  return mimeOk && extensionOk;
};

interface ProfileSettingsModalProps {
  isOpen: boolean;
  toggle: () => void;
}

const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({
  isOpen,
  toggle,
}) => {
  const dispatch = useAppDispatch();
  const { changePasswordLoading, publicKey } = useAppSelector(
    (state: any) => state.authentication
  );
  const { profilePictureUpload, userProfileImageUrl, sidebarPreferences } =
    useAppSelector((state: any) => state.master);

  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userName = loggedInUser?.fullName || loggedInUser?.name || "User";
  const userEmail = loggedInUser?.email || "";

  const [activeTab, setActiveTab] = useState<ProfileSettingsTab>("profile");
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPreview, setSelectedPreview] = useState<string | null>(null);
  const [profilePictureError, setProfilePictureError] = useState("");

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [sidebarNavMode, setSidebarNavMode] = useState<SidebarNavMode>(
    SIDEBAR_NAV_MODE_DEFAULT
  );
  const [hoverEnabled, setHoverEnabled] = useState<boolean>(
    SIDEBAR_HOVER_ENABLED_DEFAULT
  );

  const tabs = useMemo(
    () => [
      {
        id: "profile",
        label: "Profile Picture",
        icon: <Camera size={16} />,
      },
      {
        id: "password",
        label: "Password",
        icon: <Key size={16} />,
      },
      {
        id: "preferences",
        label: "Preferences",
        icon: <PanelLeft size={16} />,
      },
    ],
    []
  );

  useEffect(() => {
    if (!selectedFile) {
      setSelectedPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setSelectedPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  useEffect(() => {
    if (!isOpen) return;
    const user = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
    const fromStorage =
      typeof user?.imageUrl === "string" && user.imageUrl.trim()
        ? user.imageUrl.trim()
        : "";
    setProfileImageUrl(fromStorage || null);
    setSidebarNavMode(
      user?.preferences?.sidebar_nav_mode || SIDEBAR_NAV_MODE_DEFAULT
    );
    setHoverEnabled(
      user?.preferences?.sidebar_hover_enabled ?? SIDEBAR_HOVER_ENABLED_DEFAULT
    );
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab("profile");
      setSelectedFile(null);
      setSelectedPreview(null);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setProfilePictureError("");
      dispatch(clearProfilePictureSession());
    }
  }, [dispatch, isOpen]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    dispatch(clearProfilePictureSession());
    if (!isAllowedProfilePicture(file)) {
      setProfilePictureError("Only JPG and PNG files are allowed.");
      setSelectedFile(null);
      setSelectedPreview(null);
      event.target.value = "";
      return;
    }
    setProfilePictureError("");
    setSelectedFile(file);
  };

  const handleSaveProfilePicture = async () => {
    if (!selectedFile || !selectedPreview) return;
    if (!isAllowedProfilePicture(selectedFile)) {
      setProfilePictureError("Only JPG and PNG files are allowed.");
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      setProfilePictureError("File size must be 5MB or less.");
      return;
    }

    setProfilePictureError("");
    try {
      const uploadResponse = await dispatch(
        uploadProfilePicture(selectedFile)
      ).unwrap();

      const filename =
        uploadResponse.filename || uploadResponse.file_name || "";
      if (!filename) {
        setProfilePictureError("Upload did not return a filename.");
        return;
      }

      await dispatch(
        updateUserProfileImageUrl({ image_url: filename })
      ).unwrap();

      const displayUrl = uploadResponse.url || "";
      setProfileImageUrl(displayUrl);

      const loggedIn = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
      if (loggedIn) {
        saveToLocalStorage(STORAGE_KEYS.LOGGED_IN_USER, {
          ...loggedIn,
          imageUrl: displayUrl,
        });
      }

      setSelectedFile(null);
      setSelectedPreview(null);
    } catch {
      // Errors surface via profilePictureUpload / userProfileImageUrl in Redux
    }
  };

  const handleDiscardProfilePicture = () => {
    dispatch(clearProfilePictureSession());
    setProfilePictureError("");
    setSelectedFile(null);
    setSelectedPreview(null);
  };

  const patchStoredSidebarPreferences = (
    patch: Partial<{
      sidebar_nav_mode: SidebarNavMode;
      sidebar_hover_enabled: boolean;
    }>
  ) => {
    const loggedIn = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
    if (loggedIn) {
      saveToLocalStorage(STORAGE_KEYS.LOGGED_IN_USER, {
        ...loggedIn,
        preferences: { ...(loggedIn.preferences || {}), ...patch },
      });
    }
  };

  const handleSelectSidebarNavMode = async (mode: SidebarNavMode) => {
    if (mode === sidebarNavMode) return;
    const previousMode = sidebarNavMode;
    setSidebarNavMode(mode);
    try {
      await dispatch(
        updateSidebarPreferences({ sidebar_nav_mode: mode })
      ).unwrap();
      patchStoredSidebarPreferences({ sidebar_nav_mode: mode });
      toast.success("Sidebar preference updated");
    } catch {
      setSidebarNavMode(previousMode);
      toast.error("Failed to update sidebar preference");
    }
  };

  const handleToggleHoverEnabled = async (enabled: boolean) => {
    const previousValue = hoverEnabled;
    setHoverEnabled(enabled);
    try {
      await dispatch(
        updateSidebarPreferences({ sidebar_hover_enabled: enabled })
      ).unwrap();
      patchStoredSidebarPreferences({ sidebar_hover_enabled: enabled });
      toast.success("Sidebar preference updated");
    } catch {
      setHoverEnabled(previousValue);
      toast.error("Failed to update sidebar preference");
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (passwordErrors[name as keyof typeof passwordErrors]) {
      setPasswordErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    };

    if (!passwordData.currentPassword) {
      errors.currentPassword = "Current password is required";
    }
    if (!passwordData.newPassword) {
      errors.newPassword = "New password is required";
    }
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    }
    if (
      passwordData.newPassword &&
      passwordData.confirmPassword &&
      passwordData.newPassword !== passwordData.confirmPassword
    ) {
      errors.confirmPassword = "Passwords do not match";
    }

    setPasswordErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  const handleUpdatePassword = async () => {
    if (!validatePasswordForm()) return;

    let key = publicKey;
    if (!key) {
      const response = await dispatch(fetchPublicKey());
      if (response.meta.requestStatus === "fulfilled") {
        key = (response.payload as string) || null;
      }
    }

    if (!key) {
      toast.error("Unable to fetch encryption key. Please try again.");
      return;
    }

    let encryptedCurrentPassword = "";
    let encryptedNewPassword = "";
    try {
      [encryptedCurrentPassword, encryptedNewPassword] = await Promise.all([
        encryptWithPublicKey(key, passwordData.currentPassword),
        encryptWithPublicKey(key, passwordData.newPassword),
      ]);
    } catch (encryptionError) {
      toast.error("Password encryption failed. Please try again.");
      return;
    }

    const result = (await dispatch(
      changePassword({
        old_password: encryptedCurrentPassword,
        new_password: encryptedNewPassword,
      })
    )) as { meta?: { requestStatus?: string }; payload?: string };
    if (result?.meta?.requestStatus === "fulfilled") {
      toast.success("Password changed successfully");
      toggle();
    } else if (result?.payload) {
      toast.error(result.payload);
    }
  };

  const displayImage = selectedPreview || profileImageUrl;
  const profilePictureErrorMessage =
    profilePictureError ||
    profilePictureUpload?.error ||
    userProfileImageUrl?.error ||
    "";
  const profilePictureLoading =
    profilePictureUpload?.loading || userProfileImageUrl?.loading || false;

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title="Profile Settings"
      size="lg"
      className="profile-settings-modal"
      usePortal
      containerClassName="z-[1070]"
    >
      <div className="flex flex-col mb-2">
        <div className="mb-5 border-b border-gray-200">
          <TabsComponent
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={tabId => setActiveTab(tabId as ProfileSettingsTab)}
            variant="tab-underline"
          />
        </div>

        {activeTab === "profile" && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden">
              {displayImage ? (
                <img
                  src={displayImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Camera className="w-8 h-8 text-white" />
              )}
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">
                {userName}
              </div>
              <div className="text-sm text-gray-500">{userEmail}</div>
            </div>

            <div className="w-full max-w-md">
              <div className="text-sm font-semibold text-gray-800 mb-2">
                Upload Profile Picture
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="profile-picture-upload"
                  type="file"
                  accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <label
                  htmlFor="profile-picture-upload"
                  className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-medium cursor-pointer"
                >
                  Choose file
                </label>
                <span className="text-sm text-gray-500 truncate">
                  {selectedFile?.name || "No file chosen"}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Max file size: 5MB. Supported formats: JPG, PNG
              </div>

              {profilePictureErrorMessage && (
                <div className="text-sm text-red-600 mt-2">
                  {profilePictureErrorMessage}
                </div>
              )}

              {selectedFile && (
                <div className="mt-4 bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-700 truncate">
                    {selectedFile.name}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      variant="primary"
                      onClick={handleSaveProfilePicture}
                      className="flex-1"
                      loading={profilePictureLoading}
                      disabled={profilePictureLoading}
                    >
                      Save Profile Picture
                    </Button>
                    <Button
                      variant="gray"
                      onClick={handleDiscardProfilePicture}
                      disabled={profilePictureLoading}
                    >
                      Discard
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "password" && (
          <form
            autoComplete="off"
            className="flex flex-col gap-6"
            onSubmit={event => {
              event.preventDefault();
              handleUpdatePassword();
            }}
          >
            <Input
              label="Current Password"
              name="currentPassword"
              type="password"
              autoComplete="new-password"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              error={passwordErrors.currentPassword}
              required
            />
            <Input
              label="New Password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              error={passwordErrors.newPassword}
              required
            />
            <Input
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              error={passwordErrors.confirmPassword}
              required
            />
            <Button
              variant="primary"
              onClick={handleUpdatePassword}
              loading={changePasswordLoading}
              className="w-full"
              type="submit"
            >
              Update Password
            </Button>
          </form>
        )}

        {activeTab === "preferences" && (
          <div className="flex flex-col gap-3">
            <div className="text-sm font-semibold text-gray-800">
              Sidebar Navigation
            </div>
            <div className="flex flex-col gap-2">
              {SIDEBAR_NAV_MODE_OPTIONS.map(option => {
                const selected = option.value === sidebarNavMode;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelectSidebarNavMode(option.value)}
                    disabled={sidebarPreferences?.loading}
                    className={`flex items-start gap-3 text-left p-3 rounded-lg border transition-colors ${
                      selected
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border ${
                        selected
                          ? "bg-purple-600 border-purple-600"
                          : "border-gray-300"
                      }`}
                    >
                      {selected && <Check className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <div className="text-sm font-medium text-gray-900">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {option.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {sidebarNavMode === "hover" && (
              <div className="flex items-center justify-between gap-3 p-3 mt-1 rounded-lg border border-gray-200">
                <div className="flex flex-col gap-0.5">
                  <div className="text-sm font-medium text-gray-900">
                    Hover behaviour sidebar
                  </div>
                  <div className="text-xs text-gray-500">
                    {hoverEnabled
                      ? "On: the sidebar expands automatically when you hover over it."
                      : "Off: hover does nothing. A pin button next to the logo expands or collapses it manually."}
                  </div>
                </div>
                <ToggleSwitch
                  id="sidebar-hover-enabled-toggle"
                  checked={hoverEnabled}
                  onChange={handleToggleHoverEnabled}
                  disabled={sidebarPreferences?.loading}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </CommonModal>
  );
};

export default ProfileSettingsModal;
